import React, { useCallback, useEffect, useRef, useState } from "react";

import { FaWhatsapp } from "react-icons/fa";
import { ordersCollection } from "../utils/firebase";
import {
    ChevronDown,
    ChevronUp,
    Mail,
    Phone,
    Calendar,
    DollarSign,
    Package,
    Clock,
    CheckCircle,
    XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatFirestoreTimestamp } from "../utils/common";
import { ADMIN_TABLE_FETCH_SIZE } from "../constants";
import useDebounce from "../hooks/use-debounce";
import {
    getDocs,
    limit,
    orderBy,
    query,
    startAfter,
    where,
} from "firebase/firestore";

export default function ManageOrders() {
    const [orders, setOrders] = useState([]);
    const [lastDoc, setLastDoc] = useState(null);
    const [sort, setSort] = useState("createdAt");
    const [order, setOrder] = useState("desc");
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const size = ADMIN_TABLE_FETCH_SIZE;

    const debouncedSearchTerm = useDebounce(searchTerm, 1000);

    // A ref for the IntersectionObserver's sentinel element
    const observerRef = useRef(null);

    // Use a ref for the loading flag to avoid triggering re-creations of fetchData
    const loadingRef = useRef(false);

    const handleTableHeaderClick = (field) => {
        if (["status"].includes(field)) {
            return;
        }
        if (field === sort) {
            setOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
        } else {
            setSort(field);
            setOrder("asc");
        }
    };

    const buildQuery = useCallback(
        (reset = false) => {
            const constraints = [];

            if (debouncedSearchTerm.trim() !== "") {
                constraints.push(where("name", ">=", debouncedSearchTerm));
                constraints.push(
                    where("name", "<=", debouncedSearchTerm + "\uf8ff")
                );
                constraints.push(orderBy("name", "asc"));
            } else {
                constraints.push(orderBy(sort, order));
            }

            constraints.push(limit(size));

            if (!reset && !!lastDoc) {
                constraints.push(startAfter(lastDoc));
            }

            return query(ordersCollection, ...constraints);
        },
        [debouncedSearchTerm, lastDoc, order, size, sort]
    );

    const fetchData = useCallback(
        async (reset = false) => {
            if (loadingRef.current) return;
            loadingRef.current = true;
            setLoading(true);

            try {
                const q = buildQuery(reset);
                const snapshot = await getDocs(q);
                const fetchedOrders =
                    snapshot?.docs?.map((doc) => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                        };
                    }) ?? [];

                setOrders((prev) => {
                    if (reset) {
                        return fetchedOrders;
                    } else {
                        const arr = [...prev, ...fetchedOrders];
                        const seenIds = new Set();
                        const uniqueData = arr.filter((item) => {
                            if (seenIds.has(item.id)) {
                                return false;
                            }
                            seenIds.add(item.id);
                            return true;
                        });
                        return uniqueData;
                    }
                });

                const lastVisible =
                    snapshot.docs[snapshot.docs.length - 1] || null;
                setLastDoc(lastVisible);

                setHasMore(snapshot.docs.length === size);
            } catch (error) {
                console.error("Error fetching orders data: ", error);
            }
            loadingRef.current = false;
            setLoading(false);
        },
        [buildQuery, size]
    );

    useEffect(() => {
        setOrders([]);
        setLastDoc(null);
        setHasMore(true);
        fetchData(true);
    }, [sort, order, debouncedSearchTerm]);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            const firstEntry = entries[0];
            if (firstEntry.isIntersecting && hasMore && !loadingRef.current) {
                fetchData();
            }
        });

        const currentObserverRef = observerRef.current;
        if (currentObserverRef) {
            observer.observe(currentObserverRef);
        }
        return () => {
            if (currentObserverRef) {
                observer.unobserve(currentObserverRef);
            }
        };
    }, [hasMore, fetchData]);

    const headers = {
        title: "Order Description",
        name: "Customer Name",
        email: "Email",
        phoneNumber: "Phone",
        price: "Amount (MYR)",
        paymentMethod: "Payment Method",
        status: "Status",
        code: "Ticket Code",
        createdAt: "Created",
        updateAt: "Updated",
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "PENDING":
                return <Clock className="w-4 h-4 text-yellow-500" />;
            case "PAID":
                return <CheckCircle className="w-4 h-4 text-green-500" />;
            case "FAILED":
                return <XCircle className="w-4 h-4 text-red-500" />;
            default:
                return <Package className="w-4 h-4 text-gray-500" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "PENDING":
                return "text-yellow-600 bg-yellow-100";
            case "PAID":
                return "text-green-600 bg-green-100";
            case "FAILED":
                return "text-red-600 bg-red-100";
            default:
                return "text-gray-600 bg-gray-100";
        }
    };

    const getPaymentMethodDisplay = (method) => {
        switch (method) {
            case "masterCard":
                return "Credit Card";
            case "fpx":
                return "FPX";
            case "tng":
                return "Touch 'n Go";
            default:
                return method;
        }
    };

    return (
        <div className="flex flex-row w-full h-full">
            <div className="flex-center flex-1 items-start w-full overflow-auto h-full">
                <div className="overflow-auto w-full h-full">
                    <table className="table w-auto">
                        {/* head */}
                        <thead>
                            <tr className="[&>th]:text-center">
                                <th></th>
                                {Object.keys(headers).map((key, index) => {
                                    const header = headers[key];
                                    return (
                                        <th
                                            key={key}
                                            className="hover:bg-base-200 cursor-pointer"
                                            onClick={() =>
                                                handleTableHeaderClick(key)
                                            }
                                        >
                                            <div className="flex flex-row justify-center items-center gap-2">
                                                <p>{header}</p>
                                                {sort === key ? (
                                                    order === "desc" ? (
                                                        <ChevronUp />
                                                    ) : (
                                                        <ChevronDown />
                                                    )
                                                ) : null}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((orderItem, index) => {
                                return (
                                    <tr
                                        key={index}
                                        className="hover [&>td]:text-nowrap"
                                    >
                                        <th>{index + 1}</th>
                                        {/* Order Description */}
                                        <td>
                                            <div className="max-w-xs truncate">
                                                {orderItem.title}
                                            </div>
                                        </td>
                                        {/* Customer Name */}
                                        <td>{orderItem.name}</td>
                                        {/* Email */}
                                        <td>
                                            <div className="border flex flex-row items-center justify-between gap-1 cursor-pointer">
                                                <p
                                                    className="pl-2 truncate w-48"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(
                                                            orderItem?.email?.toLowerCase()
                                                        );
                                                        toast(
                                                            "📋 Copied Email to Clipboard"
                                                        );
                                                    }}
                                                >
                                                    {orderItem?.email?.toLowerCase()}
                                                </p>
                                                <a
                                                    className="btn btn-ghost btn-sm h-full btn-square"
                                                    href={`mailto:${orderItem?.email?.toLowerCase()}`}
                                                >
                                                    <Mail className="h-4" />
                                                </a>
                                            </div>
                                        </td>
                                        {/* Phone */}
                                        <td>
                                            <div className="border flex flex-row items-center justify-between gap-1 cursor-pointer">
                                                <p
                                                    className="pl-2 truncate w-48"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(
                                                            orderItem.phoneNumber
                                                        );
                                                        toast(
                                                            "📋 Copied Phone No to Clipboard"
                                                        );
                                                    }}
                                                >
                                                    {orderItem.phoneNumber}
                                                </p>
                                                <a
                                                    className="btn btn-ghost btn-sm h-full btn-square"
                                                    target="_blank"
                                                    href={`https://web.whatsapp.com/send/?phone=${orderItem?.phoneNumber
                                                        ?.replaceAll("+", "")
                                                        ?.replaceAll("-", "")
                                                        ?.replaceAll("-", "")}`}
                                                    rel="noreferrer"
                                                >
                                                    <FaWhatsapp className="h-4" />
                                                </a>
                                            </div>
                                        </td>
                                        {/* Price */}
                                        <td className="text-end font-mono">
                                            <div className="flex items-center justify-end gap-1">
                                                <DollarSign className="w-4 h-4" />
                                                {parseFloat(
                                                    orderItem.price
                                                ).toFixed(2)}
                                            </div>
                                        </td>
                                        {/* Payment Method */}
                                        <td className="text-center">
                                            <span className="badge badge-outline">
                                                {getPaymentMethodDisplay(
                                                    orderItem.paymentMethod
                                                )}
                                            </span>
                                        </td>
                                        {/* Status */}
                                        <td className="text-center">
                                            <div
                                                className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-sm ${getStatusColor(
                                                    orderItem.status
                                                )}`}
                                            >
                                                {getStatusIcon(
                                                    orderItem.status
                                                )}
                                                {orderItem.status}
                                            </div>
                                        </td>
                                        {/* Ticket Code */}
                                        <td className="text-center">
                                            {orderItem.code ? (
                                                <div
                                                    className="font-mono font-bold text-primary cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(
                                                            orderItem.code
                                                        );
                                                        toast(
                                                            "📋 Copied Ticket Code to Clipboard"
                                                        );
                                                    }}
                                                >
                                                    {orderItem.code}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">
                                                    -
                                                </span>
                                            )}
                                        </td>
                                        {/* Created At */}
                                        <td>
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                {formatFirestoreTimestamp(
                                                    orderItem.createdAt
                                                )}
                                            </div>
                                        </td>
                                        {/* Updated At */}
                                        <td>
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                {formatFirestoreTimestamp(
                                                    orderItem.updateAt
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="flex-center w-full p-2">
                        {/* Display messages based on state */}
                        {loading && <div className="loading loading-bars" />}
                        {!loading && orders.length === 0 && (
                            <p className="flex-center h-36">NO ORDERS</p>
                        )}
                        {!loading && orders.length > 0 && !hasMore && (
                            <p className="opacity-70">END</p>
                        )}
                    </div>
                    {/* Sentinel element for the Intersection Observer */}
                    <div ref={observerRef} style={{ height: "1px" }} />
                </div>
                <div className="divider divider-primary m-0" />
                {/* Bottom Search Bar */}
                <div className="w-full">
                    <div className="flex-center flex-row p-4 pt-2 gap-2">
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Search by Customer Name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
